import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronRight, ExternalLink, Globe, Instagram, Music, Phone, Youtube, Facebook } from 'lucide-react';
import SEO from './SEO';
import { useManagedCollection, slugify } from '../admin/useManagedCollection';
import { DJS } from './DJs';
import SocialLinks, { countSocialLinks, normalizeSocialUrl } from './SocialLinks';

const BASE_URL = 'https://dallasdesiscoop.com';

const FALLBACK_IMAGE =
  'https://storage.googleapis.com/happyspace-generated-images/generated/2026-06-24/507384bd7312.png';

// Same key function DJs.tsx uses for its managed collection, so the slug in
// the URL always matches the entry the grid card linked from.
export function djSlug(d: any, i: number) {
  return slugify(d.name || String(i));
}

// Collect every contact/social URL a DJ has, deduped, with a label and icon —
// mirrors the button logic on the grid cards.
function collectContacts(d: any) {
  const candidates: { url?: string; label?: string }[] = [
    { url: normalizeSocialUrl(d.contactUrl) },
    { url: normalizeSocialUrl(d.instagram, 'instagram'), label: 'Instagram' },
    { url: normalizeSocialUrl(d.facebook, 'facebook'), label: 'Facebook' },
    { url: normalizeSocialUrl(d.youtube), label: 'YouTube' },
    { url: normalizeSocialUrl(d.tiktok, 'tiktok'), label: 'TikTok' },
    { url: normalizeSocialUrl(d.website), label: 'Website' },
  ];
  const seen = new Set<string>();
  return candidates
    .filter((c): c is { url: string; label?: string } => !!c.url && c.url !== '#')
    .filter((c) => (seen.has(c.url) ? false : (seen.add(c.url), true)))
    .map((c) => {
      const u = c.url.toLowerCase();
      if (u.includes('facebook.com')) return { ...c, Icon: Facebook, label: 'Facebook' };
      if (u.includes('youtube.com') || u.includes('youtu.be')) return { ...c, Icon: Youtube, label: 'YouTube' };
      if (u.includes('instagram.com')) return { ...c, Icon: Instagram, label: 'Instagram' };
      return { ...c, Icon: Globe, label: c.label || 'Website' };
    });
}

export default function DJDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const mc = useManagedCollection<any>('djs', DJS, (d, i) => djSlug(d, i));

  const entry = mc.items.find((d: any, i: number) => djSlug(d, i) === slug);

  if (mc.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-8 h-8 border-2 border-[#082f6d] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!entry) {
    return (
      <div
        className="min-h-screen bg-white flex flex-col items-center justify-center px-4"
        style={{ fontFamily: "'Poppins', sans-serif" }}
      >
        <SEO
          title="DJ Not Found | Dallas Desi Scoop"
          description="This DJ listing could not be found."
          canonical="/djs"
          noindex
        />
        <p className="text-gray-500 text-lg mb-4">DJ listing not found.</p>
        <Link to="/djs" className="text-[#082f6d] font-semibold hover:underline">
          ← Back to DJs
        </Link>
      </div>
    );
  }

  const name = entry.name || 'DJ';
  const image = entry.image || FALLBACK_IMAGE;
  const contacts = collectContacts(entry);
  const phone = (entry.phone || '').trim();
  const hasAnySocial = countSocialLinks(entry) > 0;
  const canonicalPath = `/djs/${slug}`;

  const title = `${name} | Indian DJ in Dallas–Fort Worth | Dallas Desi Scoop`;
  const metaDescription = `${name} is a DJ serving the South Asian community across the Dallas–Fort Worth metroplex. Find contact details and booking info for weddings, receptions, sangeet, and desi events on Dallas Desi Scoop.`;

  const jsonLd = [
    {
      '@type': 'LocalBusiness',
      '@id': `${BASE_URL}${canonicalPath}#business`,
      name,
      image,
      description: `${name}, Indian DJ serving the Dallas–Fort Worth Desi community for weddings, receptions, sangeet, and cultural events.`,
      telephone: phone || undefined,
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Dallas',
        addressRegion: 'TX',
        addressCountry: 'US',
      },
      areaServed: 'Dallas–Fort Worth metroplex',
      url: contacts[0]?.url || `${BASE_URL}${canonicalPath}`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'DJs', item: `${BASE_URL}/djs` },
        { '@type': 'ListItem', position: 3, name, item: `${BASE_URL}${canonicalPath}` },
      ],
    },
  ];

  // Internal linking: other DJs.
  const relatedDJs = mc.items
    .map((d: any, i: number) => ({ dj: d, key: djSlug(d, i) }))
    .filter((r: any) => r.key !== slug)
    .slice(0, 6);

  return (
    <div className="min-h-screen bg-[#f9fafb]" style={{ fontFamily: "'Poppins', sans-serif" }}>
      <SEO
        title={title}
        description={metaDescription}
        canonical={canonicalPath}
        ogImage={image}
        jsonLd={jsonLd}
      />

      {/* Hero */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '260px' }}>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url("${image}")` }}
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(to bottom, rgba(8,47,109,0.75) 0%, rgba(8,47,109,0.55) 55%, rgba(8,47,109,0.88) 100%)',
          }}
        />

        <div className="relative z-10 max-w-[1000px] mx-auto px-4 pt-6 pb-10 flex flex-col">
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4 }}
            className="self-start flex items-center gap-1.5 mb-5"
          >
            <Link
              to="/djs"
              className="flex items-center gap-1.5 text-white/80 hover:text-white text-[12px] font-medium transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              DJs
            </Link>
            <ChevronRight className="w-3 h-3 opacity-60 text-white/60" />
            <span className="text-white/60 text-[12px] font-medium">{name}</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="text-white text-[26px] sm:text-[36px] font-extrabold leading-tight tracking-tight"
            style={{ textShadow: '0 2px 8px rgba(0,0,0,0.4)' }}
          >
            {name}
          </motion.h1>

          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.18 }}
            className="flex flex-wrap items-center gap-3 mt-3"
          >
            <span className="flex items-center gap-1 text-white/85 text-[13px]">
              <Music className="w-3.5 h-3.5 text-[#51ba4f]" />
              DJ · Dallas–Fort Worth
            </span>
          </motion.div>
        </div>
      </section>

      {/* Main content */}
      <div className="max-w-[1000px] mx-auto px-4 py-10 grid grid-cols-1 md:grid-cols-3 gap-8">
        <div className="md:col-span-2">
          <h2 className="text-[18px] font-bold text-gray-900 mb-3">About {name}</h2>
          <p className="text-[14px] text-gray-600 leading-relaxed max-w-[60ch]">
            {name} is a DJ serving the South Asian community in the Dallas–Fort Worth metroplex,
            bringing music and energy to desi weddings, receptions, sangeets, and cultural
            celebrations across DFW. Contact {name} directly for bookings, pricing, availability,
            and the music genres and event packages they offer.
          </p>

          {hasAnySocial && (
            <div className="mt-6">
              <SocialLinks item={entry} px={28} />
            </div>
          )}
        </div>

        {/* Contact & Info */}
        <div
          className="bg-white rounded-xl border border-gray-100 p-5 h-fit"
          style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
        >
          <h3 className="text-[13px] font-bold uppercase tracking-wide text-gray-400 mb-4">
            Contact &amp; Info
          </h3>
          <div className="space-y-3 text-[13px] text-gray-700">
            {contacts.map((c) => (
              <a
                key={c.url}
                href={c.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <c.Icon className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{c.label}</span>
              </a>
            ))}
            {phone && (
              <a
                href={`tel:${phone.replace(/[^\d+]/g, '')}`}
                className="flex items-start gap-2 hover:text-[#082f6d] transition-colors"
              >
                <Phone className="w-4 h-4 text-[#51ba4f] shrink-0 mt-0.5" />
                <span>{phone}</span>
              </a>
            )}
            {contacts.length === 0 && !phone && (
              <p className="text-gray-400 text-[13px]">Contact information coming soon.</p>
            )}
          </div>
          {contacts.length > 0 && (
            <a
              href={contacts[0].url}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 flex items-center justify-center gap-1.5 bg-[#082f6d] hover:bg-[#062454] text-white text-[13px] font-semibold py-2.5 rounded-lg transition-colors"
            >
              <ExternalLink className="w-4 h-4" />
              Contact {name}
            </a>
          )}
        </div>
      </div>

      {/* Internal links: other DJs */}
      {relatedDJs.length > 0 && (
        <div className="max-w-[1000px] mx-auto px-4 pb-8">
          <h2 className="text-[16px] font-bold text-gray-900 mb-3">More DJs in DFW</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {relatedDJs.map(({ dj, key }: any) => (
              <Link
                key={key}
                to={`/djs/${key}`}
                className="flex items-center gap-2 bg-white rounded-lg border border-gray-100 px-4 py-3 text-[13px] font-medium text-gray-700 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
              >
                <Music className="w-3.5 h-3.5 text-[#f97316] shrink-0" />
                {dj.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Internal links: explore DallasDesiScoop */}
      <div className="max-w-[1000px] mx-auto px-4 pb-12">
        <h2 className="text-[16px] font-bold text-gray-900 mb-3">Explore Dallas Desi Scoop</h2>
        <div className="flex flex-wrap gap-2">
          {[
            { to: '/djs', label: 'All DJs in DFW' },
            { to: '/events', label: 'Desi Events in DFW' },
            { to: '/party-venues', label: 'Party Venues' },
            { to: '/event-management', label: 'Event Management' },
            { to: '/restaurants', label: 'Desi Restaurants' },
            { to: '/blog', label: 'DFW Desi Guides' },
          ].map((l) => (
            <Link
              key={l.to}
              to={l.to}
              className="bg-white border border-gray-100 rounded-full px-4 py-2 text-[12px] font-medium text-gray-600 hover:text-[#f97316] hover:border-[#f97316] transition-colors"
            >
              {l.label}
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
